import React, { useEffect, useMemo, useState } from 'react';
import { Calculator, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import QuantityControl from '../components/QuantityControl';
import { api } from '../services/api';
import { usePreinvoice } from '../context/PreinvoiceContext';
import { formatWeight, parseWeight, readWeightInfo, weightLine } from '../utils/weight';

function productTitle(product) {
  return product?.title || product?.name || product?.sku || 'محصول';
}

function makeSelected(product) {
  const weight = readWeightInfo(product);
  return {
    id: product.id || product.product_item_id,
    title: productTitle(product),
    sku: product.sku || product.external_code || product.product_code || '',
    category_title: product.category_title || '',
    weight_value: weight.weight_value,
    weight_label: weight.weight_label,
    weight_unit: weight.weight_unit,
    unit: product.unit || '',
    quantity: 1,
  };
}

function WeightSummaryTable({ title, items, emptyText, editable = false, onQty, onRemove }) {
  const rows = useMemo(() => items.map((item) => ({ ...item, weight: weightLine(item) })), [items]);
  const totals = rows.reduce((sum, row) => {
    const parsed = row.weight.parsed;
    if (parsed) sum[parsed.unit] = (sum[parsed.unit] || 0) + parsed.amount * row.weight.quantity;
    return sum;
  }, {});

  return (
    <section className="rounded-3xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-extrabold text-slate-900">{title}</h2>
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#005bbb]">
          {rows.length.toLocaleString('fa-IR')} مورد
        </span>
      </div>

      {rows.length ? (
        <div className="overflow-auto">
          <table className="w-full min-w-[760px] text-center text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-4 py-3 text-center">ردیف</th>
                <th className="px-4 py-3 text-center">محصول</th>
                <th className="px-4 py-3 text-center">وزن واحد</th>
                <th className="px-4 py-3 text-center">تعداد / واحد سنجش</th>
                <th className="px-4 py-3 text-center">مجموع</th>
                {editable ? <th className="px-4 py-3" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.cart_id || row.id} className="border-t border-gray-100 transition hover:bg-slate-100/70">
                  <td className="px-4 py-3 text-gray-500">{(index + 1).toLocaleString('fa-IR')}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900">{row.title}</div>
                    {row.sku ? <div className="mt-1 text-[11px] text-slate-500">{row.sku}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {row.weight.parsed ? formatWeight(row.weight.parsed.amount, row.weight.parsed.unit) : (row.weight.weight_value || 'ثبت نشده')}
                  </td>
                  <td className="px-4 py-3">
                    {editable ? (
                      <QuantityControl value={row.quantity} onChange={(next) => onQty?.(row.id, next)} />
                    ) : (
                      Number(row.quantity || 0).toLocaleString('fa-IR')
                    )}
                    {row.unit && <div className="mt-1 text-xs text-slate-500">{row.unit}</div>}
                  </td>
                  <td className="px-4 py-3 font-extrabold text-[#005bbb]">
                    {row.weight.parsed ? formatWeight(row.weight.parsed.amount * row.weight.quantity, row.weight.parsed.unit) : '-'}
                  </td>
                  {editable ? (
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => onRemove?.(row.id)}
                        className="rounded-full p-2 text-red-500 transition hover:bg-red-50"
                        aria-label="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
              <tr className="border-t border-blue-100 bg-blue-50/60">
                <td className="px-4 py-4 font-extrabold text-slate-900" colSpan={4}>
                  مجموع کل
                </td>
                <td className="px-4 py-4 text-base font-extrabold text-[#005bbb]">
                  {Object.entries(totals).map(([unit, amount]) => <div key={unit}>{formatWeight(amount, unit)}</div>)}
                </td>
                {editable ? <td /> : null}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-8 text-center text-sm text-slate-500">{emptyText}</div>
      )}
    </section>
  );
}

export default function WeightCalculatorPage() {
  const { items } = usePreinvoice();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    let active = true;
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const search = await api.searchProducts(q.trim());
        if (active) setResults(search?.data?.products || []);
      } catch {
        if (active) setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 350);

    return () => { active = false; clearTimeout(timeout); };
  }, [q]);

  const addProduct = async (product) => {
    setLoading(true);
    let full = product;
    try {
      const res = await api.getProductById(product.id);
      full = res?.data || res?.product || product;
    } catch { /* The search result remains usable if detail loading fails. */ }
    const next = makeSelected(full);
    setSelected((prev) => {
      if (prev.some((item) => String(item.id) === String(next.id))) return prev;
      return [...prev, next];
    });
    setQ('');
    setResults([]);
    setLoading(false);
  };

  const setSelectedQty = (id, next) => {
    setSelected((prev) => prev.map((item) => (String(item.id) === String(id) ? { ...item, quantity: next } : item)));
  };

  const removeSelected = (id) => {
    setSelected((prev) => prev.filter((item) => String(item.id) !== String(id)));
  };

  const cartWeightedItems = useMemo(
    () => items.filter((item) => Boolean(readWeightInfo(item).weight_value)),
    [items]
  );

  return (
    <main className="min-h-[70vh] bg-gray-50" dir="rtl">
      <div className="pp-container py-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-[#005bbb]">
              <Calculator className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-extrabold text-slate-900">محاسبه وزن</h1>
          </div>
        </div>

        <section className="mb-6 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
          <label className="mb-2 block text-xs font-bold text-slate-500">جستجو در محصولات</label>
          <div className="relative">
            <Search className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-2xl border border-gray-200 bg-white py-3 pl-4 pr-11 text-sm outline-none transition focus:border-[#005bbb] focus:ring-2 focus:ring-blue-100"
              placeholder="نام یا کد کالا را وارد کنید..."
            />
            {(loading || results.length > 0) && (
              <div className="absolute top-full z-20 mt-2 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
                {loading ? (
                  <div className="flex items-center gap-2 px-4 py-3 text-xs text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    در حال جستجو...
                  </div>
                ) : (
                  <div className="max-h-80 overflow-auto">
                    {results.map((product) => {
                      const weight = readWeightInfo(product);
                      const parsed = parseWeight(weight.weight_value, `${weight.weight_label || ''} ${weight.weight_unit || ''}`);
                      return (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => addProduct(product)}
                          className="flex w-full items-center justify-between gap-3 border-b border-gray-100 px-4 py-3 text-right transition last:border-0 hover:bg-blue-50/60"
                        >
                          <span className="min-w-0">
                            <span className="block break-words text-sm font-bold text-slate-900">{productTitle(product)}</span>
                            <span className="mt-1 block text-[11px] text-slate-500">{product.sku || product.external_code || product.category_title || ''}</span>
                          </span>
                          <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-[#005bbb]">
                            {parsed ? formatWeight(parsed.amount, parsed.unit) : 'انتخاب'}
                            <Plus className="h-3.5 w-3.5" />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <div className="space-y-6">
          <WeightSummaryTable
            title="محصولات انتخاب‌شده برای محاسبه"
            items={selected}
            emptyText="هنوز محصول وزن‌داری برای محاسبه انتخاب نشده است."
            editable
            onQty={setSelectedQty}
            onRemove={removeSelected}
          />

          <WeightSummaryTable
            title="وزن محصولات موجود در پیش‌فاکتور شما"
            items={cartWeightedItems}
            emptyText="در سبد پیش‌فاکتور فعلی محصول وزن‌دار وجود ندارد."
          />
        </div>
      </div>
    </main>
  );
}
